import React, { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Users, FileText, Video, Shield, Tag, ChevronLeft, ChevronRight, Menu, Activity, KeyRound, ClipboardCheck } from 'lucide-react';

const navItems = [
  { to: '/admin/users',    label: 'User Management',   icon: Users,    description: 'Manage accounts and roles' },
  { to: '/admin/policies', label: 'Policy Management', icon: FileText, description: 'Upload and manage policies' },
  { to: '/admin/videos',   label: 'Video Management',  icon: Video,    description: 'Manage training videos' },
  { to: '/admin/tags',     label: 'Resource Tab', icon: Tag, description: 'Create and manage resource tabs' },
  { to: '/admin/acknowledgements', label: 'Acknowledgements', icon: ClipboardCheck, description: 'Track employee sign-offs' },
  { to: '/admin/api-tokens', label: 'API Tokens',       icon: KeyRound, description: 'Manage chatbot access' },
  { to: '/admin/logs',     label: 'Activity Logs',     icon: Activity, description: 'View admin activity history' },
];

function AdminPanel() {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50 flex">

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-30
          flex flex-col bg-white border-r border-gray-200
          transition-all duration-300 ease-in-out flex-shrink-0
          ${collapsed ? 'w-16' : 'w-64'}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Sidebar header */}
        <div className={`flex items-center border-b border-gray-100 h-16 px-3 flex-shrink-0 ${collapsed ? 'justify-center' : 'justify-between'}`}>
          {!collapsed && (
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-[#00AAFF]/10 flex items-center justify-center flex-shrink-0">
                <Shield className="w-4 h-4 text-[#00AAFF]" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-gray-900 truncate">Admin Panel</p>
                <p className="text-xs text-gray-400 truncate">Manage your portal</p>
              </div>
            </div>
          )}
          <button
            onClick={() => setCollapsed(c => !c)}
            className="hidden lg:flex w-7 h-7 rounded-lg items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors flex-shrink-0"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-1">
          {navItems.map(({ to, label, icon: Icon, description }) => {
            const isActive = location.pathname === to || (to !== '/admin' && location.pathname.startsWith(to));
            return (
              <NavLink
                key={to}
                to={to}
                onClick={() => setMobileOpen(false)}
                title={collapsed ? label : undefined}
                className={`
                  flex items-center gap-3 rounded-xl transition-all duration-150 group
                  ${collapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2.5'}
                  ${isActive
                    ? 'bg-[#00AAFF]/10 text-[#00AAFF]'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
              >
                {/* Icon */}
                <div className={`
                  w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors
                  ${isActive
                    ? 'bg-[#00AAFF]/20 text-[#00AAFF]'
                    : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200 group-hover:text-gray-700'
                  }
                `}>
                  <Icon className="w-4 h-4" />
                </div>

                {/* Label — hidden when collapsed */}
                {!collapsed && (
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{label}</p>
                    <p className="text-xs text-gray-400 truncate">{description}</p>
                  </div>
                )}

                {/* Active indicator arrow */}
                {!collapsed && isActive && (
                  <ChevronRight className="w-3.5 h-3.5 text-[#00AAFF] flex-shrink-0" />
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Collapse toggle at bottom (collapsed state only — shows expand hint) */}
        {collapsed && (
          <div className="border-t border-gray-100 py-3 flex justify-center">
            <button
              onClick={() => setCollapsed(false)}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
              title="Expand sidebar"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <div className="lg:hidden flex items-center gap-3 px-4 h-14 bg-white border-b border-gray-200">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>
          <span className="text-sm font-semibold text-gray-900">
            {navItems.find(n => location.pathname.startsWith(n.to))?.label || 'Admin Panel'}
          </span>
        </div>

        <main className="flex-1 overflow-auto p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

    </div>
  );
}

export default AdminPanel;
